import { Blocks, isEditing } from "@builder.io/sdk-react";
import classNames from "classnames";
import Image from "next/image";

import { HeroV2, HeroV2Content, HeroV2Props } from "@jti/ui/components/HeroV2";
import { Container } from "@jti/ui/eva-components/Container";

import { ResponsiveBackgroundImage } from "./ResponsiveBackgroundImage";
import { HeroDataSchema } from "./schema";

import styles from "./HeroBannerContainer.module.scss";

function isValidHeroV2Block(block: BuilderBlock): block is BuilderBlock & {
  component: { name: string; options: { data: HeroV2Content } };
} {
  return (
    !!block.component &&
    !!block.component.options &&
    typeof block.component.options.data === "object"
  );
}

type HeroContainerProps = {
  isContainerWidth: boolean;
  isViewportHeight: boolean;
  childBlocks: BuilderBlock[];
  builderBlock: BuilderBlock;
};

export function HeroBannerContainer({
  isContainerWidth,
  isViewportHeight,
  childBlocks,
  builderBlock,
}: HeroContainerProps) {
  const isChildBlocksArray = Array.isArray(childBlocks);

  const convertedData = isChildBlocksArray
    ? childBlocks
        .filter(isValidHeroV2Block)
        .map((item, index): ArrayElement<HeroV2Props["data"]> => {
          const heroData = item?.component?.options.data;
          heroData.removeVideoRoundedCorners = true;
          const validatedHeroData = HeroDataSchema.parse(heroData);

          return {
            ...validatedHeroData,
            renderBackgroundImage: (props) => (
              <ResponsiveBackgroundImage {...props} index={index} />
            ),
            renderImage: ({ src }: { src: string }) => (
              <Image
                src={src}
                alt={heroData.logoAlt}
                height={heroData.logoHeight ?? 63}
                width={heroData.logoWidth ?? 100}
              />
            ),
          };
        })
    : [];

  if (isEditing()) {
    return (
      <Blocks
        parent={builderBlock?.id}
        blocks={childBlocks}
        className={styles.editingWrapper}
      />
    );
  }

  const Wrapper = isContainerWidth ? Container : "div";

  return (
    <Wrapper
      className={classNames({
        [styles.fullWidthContainer]: !isContainerWidth,
        [styles.fullHeightContainer]: !isContainerWidth && isViewportHeight,
      })}
    >
      <HeroV2
        data={convertedData}
        isContainerWidth={isContainerWidth}
        isViewportHeight={isViewportHeight}
      />
    </Wrapper>
  );
}
